import { t, useStoreLanguage } from "../../features/store-language/i18n.ts";
import React, { useEffect, useRef, useState } from "react";
import { App, Input, notification as staticNotification } from "antd";
import PortalDialog, { PortalDialogAction } from "./PortalDialog.tsx";
import "./AppFeedback.css";
import {
  formatTraceIdMessage,
  getErrorTraceId,
  getHttpFailureMessage,
  isCanceledHttpError,
  isHandledHttpError,
  markHttpErrorHandled,
} from "../../utils/appError.ts";

type DialogIcon = "success" | "error" | "warning" | "info" | "question";

type DialogOptions = {
  title?: React.ReactNode;
  text?: React.ReactNode;
  html?: string;
  icon?: DialogIcon;
  showCancelButton?: boolean;
  showConfirmButton?: boolean;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButtonColor?: string;
  timer?: number;
  input?: "textarea" | "text";
  inputPlaceholder?: string;
  inputValue?: string;
  inputValidator?: (value: string) => string | undefined | null | Promise<string | undefined | null>;
  didOpen?: () => void;
  customClass?: { container?: string };
  width?: number | string;
  imageUrl?: string;
  imageAlt?: string;
  imageWidth?: number;
  imageHeight?: number;
  action?: React.ReactNode | ((close: () => void) => React.ReactNode);
  [key: string]: unknown;
};

type FeedbackMessage = {
  title: React.ReactNode;
  description?: React.ReactNode;
  text?: React.ReactNode;
  duration?: number;
  timer?: number;
  showConfirmButton?: boolean;
};

function normalizeFeedbackDescription(
  description: unknown,
  fallback = t("درخواست پذیرفته نشد؛ جزئیات علت در پاسخ سرور موجود نیست."),
): React.ReactNode {
  if (description === null || description === undefined || React.isValidElement(description)) {
    return description;
  }

  if (typeof description === "string") {
    return description.trim() && !/^\s*<(?:!doctype|html)\b/i.test(description) ? description : fallback;
  }

  if (typeof description === "number") {
    return description;
  }

  if (Array.isArray(description)) {
    return description.map(function (entry) {
      return normalizeFeedbackDescription(entry, fallback);
    }).filter(Boolean).join("، ") || fallback;
  }

  if (typeof description === "object") {
    const response = description as Record<string, unknown>;
    const validationErrors = response.errors;
    if (validationErrors && typeof validationErrors === "object") {
      const messages = Object.values(validationErrors as Record<string, unknown>).flatMap(function (value) {
        return Array.isArray(value) ? value.map(String) : [String(value)];
      });
      if (messages.length > 0) {
        return messages.join("، ");
      }
    }

    for (const key of ["detail", "message", "title", "error"]) {
      const value = response[key];
      if (typeof value === "string" && value.trim()) {
        return value;
      }
    }

    return fallback;
  }

  return String(description);
}

export type DialogResult = {
  isConfirmed: boolean;
  isDismissed: boolean;
  value?: any;
};

type AppApi = ReturnType<typeof App.useApp>;

let appApi: AppApi | null = null;
let visibleDialogs = 0;
let dialogSequence = 0;
type DialogRequest = { id: number; options: DialogOptions; finish: (result: DialogResult) => void };
const dialogHosts = new Map<symbol, (options: DialogOptions) => Promise<DialogResult>>();
let errorSequence = 0;
const recentlyShownTraceIds = new Map<string, number>();

function DialogInput({
  options,
  onValueChange,
}: {
  options: DialogOptions;
  onValueChange: (value: string) => void;
}) {
  useStoreLanguage();
  const [value, setValue] = useState(options.inputValue ?? "");

  function handleChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setValue(event.target.value);
    onValueChange(event.target.value);
  }

  if (options.input === "textarea") {
    return (
      <Input.TextArea
        autoFocus
        rows={4}
        value={value}
        placeholder={options.inputPlaceholder}
        onChange={handleChange}
      />
    );
  }

  return (
    <Input
      autoFocus
      value={value}
      placeholder={options.inputPlaceholder}
      onChange={handleChange}
    />
  );
}

function DialogContent({ options, onValueChange }: { options: DialogOptions; onValueChange: (value: string) => void }) {
  useStoreLanguage();
  return (
    <div className="app-feedback__content">
      {options.imageUrl ? (
        <img
          className="app-feedback__image"
          src={options.imageUrl}
          alt={options.imageAlt ?? t("تصویر پیام")}
          width={options.imageWidth}
          height={options.imageHeight}
        />
      ) : null}
      {options.text ? <div className="app-feedback__description">{options.text}</div> : null}
      {options.html ? (
        <div className="app-feedback__description" dangerouslySetInnerHTML={{ __html: options.html }} />
      ) : null}
      {options.input ? <DialogInput options={options} onValueChange={onValueChange} /> : null}
    </div>
  );
}

function normalizeOptions(
  titleOrOptions: React.ReactNode | DialogOptions,
  text?: React.ReactNode,
  icon?: DialogIcon,
): DialogOptions {
  if (typeof titleOrOptions === "object" && titleOrOptions !== null && !React.isValidElement(titleOrOptions)) {
    return titleOrOptions as DialogOptions;
  }

  return { title: titleOrOptions, text, icon };
}

function openNotification(options: DialogOptions): Promise<DialogResult> {
  const notification = appApi?.notification ?? staticNotification;
  const key = `app-feedback-${Date.now()}-${Math.random()}`;
  const duration = options.timer
    ? options.timer / 1000
    : options.icon === "error"
      ? 8
      : options.icon === "warning"
        ? 6
        : 4.5;

  const notificationType = options.icon === "question" ? "info" : options.icon ?? "info";
  const closeNotification = function () {
    notification.destroy(key);
  };
  const action = typeof options.action === "function"
    ? options.action(closeNotification)
    : options.action;
  notification[notificationType]({
    key,
    message: options.title,
    description: options.text,
    placement: "bottomRight",
    duration,
    showProgress: true,
    pauseOnHover: true,
    className: "app-feedback-notification",
    btn: action,
  });

  return Promise.resolve({ isConfirmed: false, isDismissed: true });
}

async function show(
  titleOrOptions: React.ReactNode | DialogOptions,
  text?: React.ReactNode,
  icon?: DialogIcon,
): Promise<DialogResult> {
  const options = normalizeOptions(titleOrOptions, text, icon);
  const isSimpleFeedback =
    typeof titleOrOptions !== "object" ||
    (options.showConfirmButton === false && Boolean(options.timer));

  if (isSimpleFeedback) {
    return openNotification(options);
  }

  const host = Array.from(dialogHosts.values()).pop();
  return host ? host(options) : { isConfirmed: false, isDismissed: true };

}

const AppFeedback = {
  notify: show,
  decide: show,
  success: function (message: FeedbackMessage) {
    return openNotification({ ...message, text: message.description ?? message.text, icon: "success" });
  },
  error: function (message: FeedbackMessage) {
    errorSequence += 1;
    return openNotification({
      ...message,
      text: normalizeFeedbackDescription(message.description ?? message.text),
      icon: "error",
    });
  },
  httpError: function (error: unknown) {
    if (isCanceledHttpError(error)) {
      markHttpErrorHandled(error);
      return Promise.resolve({ isConfirmed: false, isDismissed: true });
    }

    if (isHandledHttpError(error)) {
      return Promise.resolve({ isConfirmed: false, isDismissed: true });
    }

    markHttpErrorHandled(error);

    const response = (error as { response?: { status?: number; data?: unknown } } | null)?.response;
    if (response?.status && response.status >= 400 && response.status < 500) {
      // Correlation IDs are attached to expected failures too. They must not
      // replace business/validation messages or trigger trace-ID deduplication.
      return AppFeedback.error({
        title: t("خطا!"),
        description: normalizeFeedbackDescription(
          response.data ?? getHttpFailureMessage(error),
          getHttpFailureMessage(error),
        ),
      });
    }

    const traceId = getErrorTraceId(error);
    if (traceId && response?.status && response.status >= 500) {
      return AppFeedback.unhandledError(traceId, getHttpFailureMessage(error));
    }

    return AppFeedback.error({
      title: t("خطا!"),
      description: getHttpFailureMessage(error),
    });
  },
  unhandledError: function (traceId: string, description = formatTraceIdMessage(traceId)) {
    const now = Date.now();
    const lastShownAt = recentlyShownTraceIds.get(traceId) ?? 0;
    if (now - lastShownAt < 10000) {
      return Promise.resolve({ isConfirmed: false, isDismissed: true });
    }

    recentlyShownTraceIds.set(traceId, now);
    errorSequence += 1;
    return openNotification({
      title: t("درخواست انجام نشد"),
      text: description,
      icon: "error",
    });
  },
  warning: function (message: FeedbackMessage) {
    return openNotification({ ...message, text: message.description ?? message.text, icon: "warning" });
  },
  info: function (message: FeedbackMessage) {
    return openNotification({ ...message, text: message.description ?? message.text, icon: "info" });
  },
  confirm: function (options: DialogOptions) {
    return show({ ...options, showCancelButton: true, icon: options.icon ?? "question" });
  },
  confirmDestructive: function (options: DialogOptions) {
    return show({ ...options, showCancelButton: true, icon: options.icon ?? "warning" });
  },
  prompt: function (options: DialogOptions) {
    return show({ ...options, showCancelButton: true });
  },
  isVisible: function () { return visibleDialogs > 0; },
  getConfirmButton: function () {
    return document.querySelector<HTMLButtonElement>(".portal-dialog .portal-dialog__actions .ant-btn-primary");
  },
  getErrorSequence: function () {
    return errorSequence;
  },
};

function FeedbackDialog({ request }: { request: DialogRequest }) {
  const { options, finish } = request;
  const inputValue = useRef(options.inputValue ?? "");
  const locked = useRef(false);
  const [pending, setPending] = useState(false);
  async function accept() {
    if (locked.current) return;
    locked.current = true;
    setPending(true);
    try {
      const validation = await options.inputValidator?.(inputValue.current);
      if (validation) {
        AppFeedback.warning({ title: validation });
        return;
      }
      finish({ isConfirmed: true, isDismissed: false, value: options.input ? inputValue.current : true });
    } catch (error) {
      AppFeedback.httpError(error);
    } finally {
      locked.current = false;
      setPending(false);
    }
  }
  function cancel() {
    if (!locked.current) finish({ isConfirmed: false, isDismissed: true });
  }
  return (
    <PortalDialog
      open
      title={options.title}
      width={options.width}
      busy={pending}
      destructive={options.icon === "warning" || options.icon === "error"}
      onClose={cancel}
      afterOpenChange={function (open) { if (open) options.didOpen?.(); }}
      footer={(
        <>
          {options.showConfirmButton !== false ? (
            <PortalDialogAction
              primary
              danger={options.icon === "warning" || options.icon === "error"}
              pending={pending}
              onClick={function () { void accept(); }}
            >
              {options.confirmButtonText ?? t("تأیید")}
            </PortalDialogAction>
          ) : null}
          {options.showCancelButton ? (
            <PortalDialogAction cancel disabled={pending} onClick={cancel}>
              {options.cancelButtonText ?? t("انصراف")}
            </PortalDialogAction>
          ) : null}
        </>
      )}
    >
      <DialogContent options={options} onValueChange={function (value) { inputValue.current = value; }} />
    </PortalDialog>
  );
}

export function AppFeedbackBridge() {
  useStoreLanguage();
  const api = App.useApp();
  const [requests, setRequests] = useState<DialogRequest[]>([]);
  const active = useRef(new Map<number, DialogRequest>());
  appApi = api;

  useEffect(function () {
    const key = Symbol("feedback-host");
    const pending = active.current;
    dialogHosts.set(key, function (options) {
      return new Promise<DialogResult>(function (resolve) {
        const id = ++dialogSequence;
        const request: DialogRequest = { id, options, finish: function (result) {
          if (!pending.delete(id)) return;
          visibleDialogs = Math.max(0, visibleDialogs - 1);
          setRequests(Array.from(pending.values()));
          resolve(result);
        } };
        pending.set(id, request);
        visibleDialogs += 1;
        setRequests(Array.from(pending.values()));
      });
    });
    return function () {
      dialogHosts.delete(key);
      for (const request of pending.values()) request.finish({ isConfirmed: false, isDismissed: true });
      if (appApi === api) appApi = null;
    };
  }, [api]);

  return (
    <>
      {requests.map(function (request) {
        return <FeedbackDialog key={request.id} request={request} />;
      })}
    </>
  );
}

export default AppFeedback;
